// Dashboard with 3D RPG View - Three.js Enabled v2.1
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ClipboardList, FileText, Bell, LogOut, Flame, Zap, Workflow, Gamepad2 } from 'lucide-react';
import KanbanBoard from './KanbanBoard';
import ActivityFeed from './ActivityFeed';
import DocumentPanel from './DocumentPanel';
import TaskDetail from './TaskDetail';
import FinancialDashboard from './FinancialDashboard';
import GamificationPanel from './GamificationPanel';
import OpsPanel from './OpsPanel';
import RunsPanel from './RunsPanel';
import SkillsPanel from './SkillsPanel';
import RPG3DView from './RPG3DView';
import type { Task, Activity as ActivityType, Agent, Document as DocumentType } from '../types';
import { useTasks, useActivities, useAgents, useDocuments, useMoveTask, useCreateTask, useCreateActivity, useDeleteTask, useCreateDocument, useMissions, useCreateMission, useCombinedProposals, useCombinedMissions, useOpsSteps, useOpsEvents, useApproveProposal, useRejectProposal, useOpsActionRuns, useOpsApprovals, useApproveApprovalRequest, useRejectApprovalRequest } from '../hooks/useConvexData';
import type { Id } from '../convex-types';

import './KanbanBoard.css';
import './ActivityFeed.css';
import './DocumentPanel.css';
import './TaskDetail.css';
import './FinancialDashboard.css';
import './GamificationPanel.css';
import './Header.css';

type ViewMode = 'board' | 'activity' | 'documents' | 'ops' | 'runs' | 'skills' | 'rpg' | 'chars';

interface DashboardProps {
  onLogout: () => void;
}

// Smart Defaults - localStorage keys
const STORAGE_KEYS = {
  LAST_VIEW: 'mc_last_view',
  LAST_COLUMN: 'mc_last_column',
  FILTER_ASSIGNEE: 'mc_filter_assignee',
  TIME_RANGE: 'mc_time_range',
  STREAK_COUNT: 'mc_streak_count',
  LAST_STREAK_DATE: 'mc_last_streak_date',
};

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  // Smart Defaults: Load from localStorage
  const [currentView, setCurrentView] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.LAST_VIEW);
    return (saved as ViewMode) || 'board';
  });
  
  const [selectedTaskId, setSelectedTaskId] = useState<Id<"tasks"> | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [activeMissionId, setActiveMissionId] = useState<Id<"missions"> | null>(null);
  
  // Smart Defaults: Filter settings
  const [filterAssignee, setFilterAssignee] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.FILTER_ASSIGNEE) || 'me';
  });
  
  const [timeRange, setTimeRange] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.TIME_RANGE) || 'this_week';
  });

  // Smart Defaults: Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.LAST_VIEW, currentView);
  }, [currentView]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FILTER_ASSIGNEE, filterAssignee);
  }, [filterAssignee]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.TIME_RANGE, timeRange);
  }, [timeRange]);

  // Convex data
  const missions = useMissions();
  const tasks = useTasks(activeMissionId || undefined);
  const activities = useActivities(activeMissionId || undefined);
  const agents = useAgents();
  const documents = useDocuments(activeMissionId || undefined);
  const moveTask = useMoveTask();
  const createTask = useCreateTask();
  const createActivity = useCreateActivity();
  const deleteTask = useDeleteTask();
  const createDocument = useCreateDocument();
  const createMission = useCreateMission();

  // Ops loop data
  const opsProposals = useCombinedProposals();
  const opsMissions = useCombinedMissions();
  const opsSteps = useOpsSteps();
  const opsEvents = useOpsEvents();
  const opsActionRuns = useOpsActionRuns();
  const opsApprovals = useOpsApprovals();
  const approveProposal = useApproveProposal();
  const rejectProposal = useRejectProposal();
  const approveApprovalRequest = useApproveApprovalRequest();
  const rejectApprovalRequest = useRejectApprovalRequest();

  // Loading state
  const isLoading = tasks === undefined || activities === undefined || agents === undefined || missions === undefined;

  useEffect(() => {
    if (!missions || missions.length === 0) return;
    if (!activeMissionId) {
      setActiveMissionId(missions[0]._id as Id<"missions">);
    }
  }, [missions, activeMissionId]);

  const activeMission = missions?.find(m => m._id === activeMissionId) || missions?.[0] || null;
  const ariseAgentId = getAriseAgentId(agents);
  const activeAgentId = getActiveAgentId(agents);

  // Only use agent IDs that are valid Convex IDs (not fallback strings)
  const validAriseAgentId = agents?.find(a => a._id === ariseAgentId)?._id;
  const validActiveAgentId = agents?.find(a => a._id === activeAgentId)?._id;
  const coordinatorAgentId = validActiveAgentId || validAriseAgentId;

  // Filter tasks based on Smart Defaults
  const filteredTasks = React.useMemo(() => {
    if (!tasks) return [];
    
    let filtered = tasks;
    
    // Filter by assignee (default: only show MY tasks)
    if (filterAssignee === 'me') {
      filtered = filtered.filter(t => 
        t.assigneeIds.includes(activeAgentId) || t.assigneeIds.length === 0
      );
    }
    
    // Filter by time range (default: this week)
    if (timeRange === 'this_week') {
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(t => (t._creationTime || 0) > oneWeekAgo);
    } else if (timeRange === 'today') {
      const today = new Date().setHours(0, 0, 0, 0);
      filtered = filtered.filter(t => (t._creationTime || 0) > today);
    }
    
    return filtered;
  }, [tasks, filterAssignee, ariseAgentId, timeRange]);

  // Calculate unread notifications
  const unreadCount = activities?.filter(a => !a.read).length || 0;

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if in input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      // J/K for next/prev task
      if (e.key === 'j' || e.key === 'J') {
        const currentIndex = filteredTasks.findIndex(t => t._id === selectedTaskId);
        const nextTask = filteredTasks[currentIndex + 1];
        if (nextTask) {
          setSelectedTaskId(nextTask._id as Id<"tasks">);
        }
      } else if (e.key === 'k' || e.key === 'K') {
        const currentIndex = filteredTasks.findIndex(t => t._id === selectedTaskId);
        const prevTask = filteredTasks[currentIndex - 1];
        if (prevTask) {
          setSelectedTaskId(prevTask._id as Id<"tasks">);
        }
      }
      
      // Escape to close modal
      if (e.key === 'Escape') {
        setShowTaskDetail(false);
      }
      
      // N for new task
      if (e.key === 'n' || e.key === 'N') {
        // Trigger new task creation - handled in KanbanBoard
      }
      
      // Number keys for views
      if (e.key === '1') setCurrentView('board');
      if (e.key === '2') setCurrentView('activity');
      if (e.key === '3') setCurrentView('agents');
      if (e.key === '4') setCurrentView('documents');
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredTasks, selectedTaskId]);

  const handleTaskMove = async (taskId: string, newStatus: Task['status']) => {
    if (!activeMissionId) return;
    const convexStatus = mapStatusToConvex(newStatus);
    await moveTask({ 
      taskId: taskId as Id<"tasks">, 
      newStatus: convexStatus 
    });
    
    await createActivity({
      missionId: activeMissionId,
      type: 'task_moved',
      agentId: activeAgentId,
      message: `Moved task to ${newStatus.replace('_', ' ')}`,
      taskId: taskId as Id<"tasks">,
    });
  };

  const handleTaskClick = (taskId: Id<"tasks">) => {
    setSelectedTaskId(taskId);
    setShowTaskDetail(true);
  };

  const handleCreateTask = async (title: string, description: string) => {
    if (!activeMissionId) return;
    const newTaskId = await createTask({
      missionId: activeMissionId,
      title,
      description,
      status: 'inbox',
      assigneeIds: [activeAgentId],
      priority: 'medium',
      tags: [],
    });

    await createActivity({
      missionId: activeMissionId,
      type: 'task_created',
      agentId: activeAgentId,
      message: `Created task: ${title}`,
      taskId: newTaskId,
    });
  };

  const handleCreateMission = async (title: string, description: string) => {
    const missionId = await createMission({
      name: title,
      description,
      createdBy: activeAgentId,
    });

    setActiveMissionId(missionId);

    await createActivity({
      missionId,
      type: 'task_created',
      agentId: activeAgentId,
      message: `Created mission: ${title}`,
      taskId: undefined,
    });
  };

  const handleArchiveTask = async (taskId: string) => {
    if (!activeMissionId) return;
    await deleteTask({ taskId: taskId as Id<"tasks"> });

    await createActivity({
      missionId: activeMissionId,
      type: 'task_moved',
      agentId: activeAgentId,
      message: `Archived task`,
      taskId: taskId as Id<"tasks">,
    });
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!activeMissionId) return;
    await deleteTask({ taskId: taskId as Id<"tasks"> });

    await createActivity({
      missionId: activeMissionId,
      type: 'task_moved',
      agentId: activeAgentId,
      message: 'Deleted task',
      taskId: taskId as Id<"tasks">,
    });

    setShowTaskDetail(false);
    setSelectedTaskId(null);
  };

  const handleCloseTaskDetail = () => {
    setShowTaskDetail(false);
    setSelectedTaskId(null);
  };

  // Batch Actions
  const handleArchiveAllDone = async () => {
    const doneTasks = filteredTasks.filter(t => t.status === 'done');
    for (const task of doneTasks) {
      await handleArchiveTask(task._id);
    }
    await createActivity({
      missionId: activeMissionId!,
      type: 'task_moved',
      agentId: activeAgentId,
      message: `Archived ${doneTasks.length} completed tasks`,
      taskId: undefined,
    });
  };

  const selectedTask = tasks?.find(t => t._id === selectedTaskId) || null;

  // Convert Convex docs to our Task type
  const convertedTasks: Task[] = filteredTasks.map(t => convertConvexTask(t, agents)) || [];
  const convertedActivities: ActivityType[] = activities?.map(a => convertConvexActivity(a, agents)) || [];
  const convertedAgents: Agent[] = agents?.map(a => convertConvexAgent(a)) || [];
  const convertedDocuments: DocumentType[] = documents?.map(d => convertConvexDocument(d, agents)) || [];

  // Calculate gamification stats
  const doneThisWeek = filteredTasks.filter(t => {
    if (t.status !== 'done') return false;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return (t._creationTime || 0) > oneWeekAgo;
  }).length;

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading Mission Control...</p>
        </div>
      </div>
    );
  }

  const mockRevenue = {
    daily: { current: 0, target: 1000, change: 0 },
    monthly: { current: 0, target: 30000, change: 0 },
    annual: { current: 0, target: 360000, change: 0 },
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-brand">
          <div className="brand-crest">👑</div>
          <div className="brand-text"><h1>Mission Control</h1></div>
          <select
            className="mission-select"
            value={activeMissionId || ''}
            onChange={(e) => setActiveMissionId(e.target.value as Id<"missions">)}
          >
            {missions?.map((m: any) => (
              <option key={m._id} value={m._id}>{m.name}</option>
            ))}
          </select>
        </div>

        <nav className="header-nav">
          <button className={`nav-btn ${currentView === 'board' ? 'active' : ''}`} onClick={() => setCurrentView('board')}>
            <ClipboardList size={14} /> Board
          </button>
          <button className={`nav-btn ${currentView === 'activity' ? 'active' : ''}`} onClick={() => setCurrentView('activity')}>
            <Activity size={14} /> Activity
            {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
          </button>
          <button className={`nav-btn ${currentView === 'documents' ? 'active' : ''}`} onClick={() => setCurrentView('documents')}>
            <FileText size={14} /> Docs
          </button>
          <button className={`nav-btn ${currentView === 'ops' ? 'active' : ''}`} onClick={() => setCurrentView('ops')}>
            <Workflow size={14} /> Autopilot
          </button>
          <button className={`nav-btn ${currentView === 'runs' ? 'active' : ''}`} onClick={() => setCurrentView('runs')}>
            <FileText size={14} /> Runs
          </button>
          <button className={`nav-btn ${currentView === 'skills' ? 'active' : ''}`} onClick={() => setCurrentView('skills')}>
            <Bell size={14} /> Skills
          </button>
          <button className={`nav-btn ${currentView === 'rpg' ? 'active' : ''}`} onClick={() => setCurrentView('rpg')}>
            <Gamepad2 size={14} /> RPG
          </button>
        </nav>

        <div className="header-actions">
          <select className="filter-pill" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
            <option value="me">My Tasks</option>
            <option value="all">All Tasks</option>
          </select>
          <select className="filter-pill" value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="this_week">This Week</option>
            <option value="today">Today</option>
            <option value="all">All Time</option>
          </select>
          <button className="logout-btn" onClick={onLogout}><LogOut size={14} /></button>
        </div>
      </header>

      <main className="dashboard-main">
        {currentView === 'board' && (
          <>
            <div className="view-header">
              <h2>Task Board</h2>
              <p className="view-subtitle">Manual task management — create, organize, and track work</p>
            </div>
            <FinancialDashboard revenue={mockRevenue} />
            
            {/* Batch Actions Bar */}
            <div className="batch-actions-bar">
              <span className="batch-label">Batch Actions:</span>
              <button 
                className="batch-btn"
                onClick={handleArchiveAllDone}
                disabled={!convertedTasks.some(t => t.status === 'done')}
              >
                <Zap size={14} />
                Archive All Done
              </button>
              <div className="keyboard-hint">
                Press <kbd>J</kbd>/<kbd>K</kbd> to navigate • <kbd>1-4</kbd> for views • <kbd>Esc</kbd> to close
              </div>
            </div>
            
            <KanbanBoard 
              tasks={convertedTasks}
              agents={convertedAgents}
              onTaskMove={handleTaskMove}
              onTaskClick={handleTaskClick}
              onCreateTask={handleCreateTask}
              onCreateMission={handleCreateMission}
              onArchiveTask={handleArchiveTask}
              onDeleteTask={handleDeleteTask}
              selectedTaskId={selectedTaskId}
            />
          </>
        )}
        {currentView === 'activity' && (
          <ActivityFeed 
            activities={convertedActivities}
            agents={convertedAgents}
          />
        )}
        {currentView === 'documents' && (
          <DocumentPanel 
            documents={convertedDocuments}
            tasks={convertedTasks}
            onCreateDocument={async ({ title, content, type }) => {
              if (!activeMissionId) return;
              const newDocId = await createDocument({
                missionId: activeMissionId,
                title,
                content,
                type,
                authorId: activeAgentId,
              });

              await createActivity({
                missionId: activeMissionId,
                type: 'document_created',
                agentId: activeAgentId,
                message: `Created document: ${title}`,
                taskId: undefined,
              });

              return newDocId;
            }}
          />
        )}
        {currentView === 'ops' && (
          <>
            <div className="view-header">
              <h2>Autopilot</h2>
              <p className="view-subtitle">Agent-driven operations — review proposals, approve missions, monitor activity</p>
            </div>
            <OpsPanel
              approvals={opsApprovals || []}
              proposals={opsProposals || []}
              missions={opsMissions || []}
              steps={opsSteps || []}
              events={opsEvents || []}
              onApprove={(proposalId) => {
                if (!coordinatorAgentId) {
                  alert('Agent data not loaded yet. Please wait a moment and try again.');
                  return;
                }
                approveProposal({ proposalId, approvedBy: coordinatorAgentId });
              }}
              onReject={(proposalId) => {
                if (!coordinatorAgentId) {
                  alert('Agent data not loaded yet. Please wait a moment and try again.');
                  return;
                }
                rejectProposal({ proposalId, rejectedBy: coordinatorAgentId });
              }}
              onApproveApproval={(approvalId) => {
                if (!coordinatorAgentId) {
                  alert('Agent data not loaded yet. Please wait a moment and try again.');
                  return;
                }
                approveApprovalRequest({ approvalId, approvedBy: coordinatorAgentId });
              }}
              onRejectApproval={(approvalId) => {
                if (!coordinatorAgentId) {
                  alert('Agent data not loaded yet. Please wait a moment and try again.');
                  return;
                }
                rejectApprovalRequest({ approvalId, approvedBy: coordinatorAgentId });
              }}
            />
          </>
        )}
        {currentView === 'runs' && (
          <>
            <div className="view-header">
              <h2>Runs</h2>
              <p className="view-subtitle">Run history + evidence bundle viewer</p>
            </div>
            <RunsPanel
              actionRuns={opsActionRuns || []}
              documents={convertedDocuments}
            />
          </>
        )}
        {currentView === 'skills' && (
          <>
            <div className="view-header">
              <h2>Skills</h2>
              <p className="view-subtitle">Catalog of installed skills (SSOT)</p>
            </div>
            <SkillsPanel />
          </>
        )}
        {currentView === 'rpg' && (
          <RPG3DView />
        )}
      </main>

      {showTaskDetail && selectedTask && (
        <TaskDetail 
          task={selectedTask}
          onClose={handleCloseTaskDetail}
          onStatusChange={(status) => handleTaskMove(selectedTask._id, status)}
          onDelete={handleDeleteTask}
          agents={convertedAgents}
          documents={convertedDocuments}
          activities={convertedActivities}
        />
      )}
    </div>
  );
};

// Helper functions
function mapStatusToConvex(status: string): "inbox" | "assigned" | "blocked" | "in_progress" | "review" | "done" {
  const statusMap: Record<string, "inbox" | "assigned" | "blocked" | "in_progress" | "review" | "done"> = {
    'todo': 'inbox',
    'inbox': 'inbox',
    'assigned': 'assigned',
    'blocked': 'blocked',
    'in_progress': 'in_progress',
    'review': 'review',
    'done': 'done',
  };
  return statusMap[status] || 'inbox';
}

function mapConvexStatusToFrontend(status: string): 'todo' | 'blocked' | 'in_progress' | 'review' | 'done' {
  const statusMap: Record<string, 'todo' | 'blocked' | 'in_progress' | 'review' | 'done'> = {
    'inbox': 'todo',
    'assigned': 'todo',
    'blocked': 'blocked',
    'in_progress': 'in_progress',
    'review': 'review',
    'done': 'done',
  };
  return statusMap[status] || 'todo';
}

function getAriseAgentId(agents: any[] | undefined): Id<"agents"> {
  if (!agents) return 'peter' as Id<"agents">;
  const peter = agents.find(a => a.name === 'Peter');
  return peter?._id || ('peter' as Id<"agents">);
}

function getActiveAgentId(agents: any[] | undefined): Id<"agents"> {
  if (!agents || agents.length === 0) return 'peter' as Id<"agents">;
  try {
    const stored = localStorage.getItem('mc:actor');
    if (stored) {
      const found = agents.find(a => (a.name || '').toLowerCase() === stored.toLowerCase());
      if (found) return found._id;
    }
    // Default to James if present, else Peter
    const james = agents.find(a => (a.name || '').toLowerCase() === 'james');
    if (james) {
      localStorage.setItem('mc:actor', 'James');
      return james._id;
    }
    const peter = agents.find(a => (a.name || '').toLowerCase() === 'peter');
    if (peter) {
      localStorage.setItem('mc:actor', 'Peter');
      return peter._id;
    }
  } catch (e) {
    // ignore localStorage errors
  }
  return agents[0]._id;
}

function convertConvexTask(task: any, agents: any[] | undefined): Task {
  return {
    id: task._id,
    _id: task._id,
    missionId: task.missionId,
    title: task.title,
    description: task.description,
    status: mapConvexStatusToFrontend(task.status),
    assigneeIds: task.assigneeIds,
    priority: task.priority,
    tags: task.tags || [],
    createdAt: task._creationTime ? new Date(task._creationTime) : new Date(),
    updatedAt: task._creationTime ? new Date(task._creationTime) : new Date(),
  };
}

function convertConvexActivity(activity: any, agents: any[] | undefined): ActivityType {
  return {
    id: activity._id,
    _id: activity._id,
    missionId: activity.missionId,
    type: activity.type,
    agentId: activity.agentId,
    message: activity.message,
    taskId: activity.taskId,
    createdAt: activity._creationTime ? new Date(activity._creationTime) : new Date(),
    read: activity.read || false,
  };
}

function convertConvexAgent(agent: any): Agent {
  return {
    id: agent._id,
    _id: agent._id,
    name: agent.name,
    role: agent.role,
    status: agent.status,
    currentTaskId: agent.currentTaskId,
    emoji: agent.emoji,
  };
}

function convertConvexDocument(doc: any, agents: any[] | undefined): DocumentType {
  const author = agents?.find(a => a._id === doc.authorId);
  return {
    id: doc._id,
    _id: doc._id,
    missionId: doc.missionId,
    title: doc.title,
    content: doc.content,
    type: doc.type,
    authorId: doc.authorId,
    taskId: doc.taskId,
    createdAt: doc._creationTime ? new Date(doc._creationTime) : new Date(),
    updatedAt: doc._creationTime ? new Date(doc._creationTime) : new Date(),
  };
}

export default Dashboard;
